import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { DollarSign, Sparkles } from "lucide-react";
import api from "../lib/api";

const POLL_MS = 20000;
const LAST_SEEN_KEY = "vortex_admin_last_order_seen";
const UNREAD_KEY = "vortex_admin_unread_orders";

/**
 * Polls /admin/analytics/recent-orders every 20s and surfaces a celebratory
 * toast for every new paid order. Tracks the latest `paid_at` in localStorage
 * and exposes the unread count via the same key for the tab badge.
 */
export const useAdminOrderNotifier = () => {
    const [unread, setUnread] = useState(() => {
        try { return parseInt(localStorage.getItem(UNREAD_KEY) || "0", 10); } catch { return 0; }
    });
    const seededRef = useRef(false);

    useEffect(() => {
        let cancelled = false;
        const token = localStorage.getItem("admin_token");
        if (!token) return undefined;

        const tick = async () => {
            try {
                const since = localStorage.getItem(LAST_SEEN_KEY) || undefined;
                const params = since ? { since, limit: 20 } : { limit: 1 };
                const { data } = await api.get("/admin/analytics/recent-orders", {
                    params,
                    headers: { "x-admin-token": token },
                });
                if (cancelled) return;

                const orders = (data?.orders || []).filter((o) => o.paid_at);
                if (!seededRef.current && !since && orders.length > 0) {
                    // First load with no previous timestamp — seed without toasting.
                    localStorage.setItem(LAST_SEEN_KEY, orders[0].paid_at);
                    seededRef.current = true;
                    return;
                }
                seededRef.current = true;

                if (orders.length === 0) return;

                // Oldest → newest so toasts stack naturally
                const ordered = [...orders].reverse();
                for (const o of ordered) {
                    const amt = Number(o.amount || 0).toFixed(2);
                    const loc = o.location ? ` from ${o.location}` : "";
                    toast.success(
                        `Order ${o.reference} · A$${amt}${loc}`,
                        {
                            description: `${o.item_count} item${o.item_count === 1 ? "" : "s"} · ${o.customer_name || "Customer"}`,
                            icon: <DollarSign size={14} className="text-emerald-400" />,
                            duration: 8000,
                        }
                    );
                }
                const latest = orders[0].paid_at;
                localStorage.setItem(LAST_SEEN_KEY, latest);
                setUnread((prev) => {
                    const next = prev + orders.length;
                    localStorage.setItem(UNREAD_KEY, String(next));
                    return next;
                });
            } catch {
                // Token invalid or network — silent
            }
        };

        tick();
        const id = setInterval(tick, POLL_MS);
        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, []);

    const clearUnread = () => {
        localStorage.setItem(UNREAD_KEY, "0");
        setUnread(0);
    };

    return { unread, clearUnread };
};

export const NewOrderBadge = ({ count }) => {
    if (!count || count <= 0) return null;
    return (
        <span
            className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-[var(--tiffany)] text-black text-[10px] font-mono font-bold animate-pulse"
            data-testid="new-orders-badge"
            title={`${count} new ${count === 1 ? "order" : "orders"} since you last checked`}
        >
            <Sparkles size={9} className="mr-0.5" />
            {count > 99 ? "99+" : count}
        </span>
    );
};
